import { FunctionComponent, useState, useMemo } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, FormControl, InputLabel, Select, MenuItem } from '@mui/material';
import { useTranslation } from '@/lib';
import { TransactionStatus } from '../api/transactions/transaction.contracts';
import { transactionService } from '../api/transactions/transaction.service';
import { toast } from 'react-toastify';
import { getTransactionStatusStyle, TransactionModel } from '../types/TransactionModel';

interface BulkUpdateStatusModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedTransactions: TransactionModel[];
  onStatusUpdated: () => void;
}

export const BulkUpdateStatusModal: FunctionComponent<BulkUpdateStatusModalProps> = ({
  isOpen,
  onClose,
  selectedTransactions,
  onStatusUpdated,
}) => {
  const { t } = useTranslation();
  const [selectedStatus, setSelectedStatus] = useState<TransactionStatus>(TransactionStatus.InProgress);
  const [isUpdating, setIsUpdating] = useState(false);

  // Check if any selected transactions are finalized
  const hasFinalizedTransactions = useMemo(() => {
    return selectedTransactions.some((transaction) => transaction.status === TransactionStatus.Finalized);
  }, [selectedTransactions]);

  const handleUpdateStatus = async () => {
    setIsUpdating(true);

    try {
      const transactionIds = selectedTransactions.map((transaction) => transaction.id);
      const response = await transactionService.bulkUpdateStatus(transactionIds, selectedStatus);

      if (response.isSuccess) {
        toast.success(t('bulk_transaction_status_updated', { count: selectedTransactions.length }));
        onStatusUpdated();
        onClose();
      } else {
        toast.error(t('error_updating_transaction_status'));
      }
    } catch (error) {
      toast.error(t('error_updating_transaction_status'));
    } finally {
      setIsUpdating(false);
    }
  };

  const getStatusLabel = (status: TransactionStatus) => {
    switch (status) {
      case TransactionStatus.InProgress:
        return t('in_progress');
      case TransactionStatus.Finalized:
        return t('finalized');
      default:
        return t('unknown');
    }
  };

  return (
    <Dialog open={isOpen} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t('bulk_update_transaction_status')}</DialogTitle>
      <DialogContent>
        <div className="mb-4 text-sm">{t('bulk_update_status_description', { count: selectedTransactions.length })}</div>
        {hasFinalizedTransactions && (
          <div className="mb-4 text-sm text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-800/20 p-3 rounded-md">
            {t('finalized_transactions_not_updated')}
          </div>
        )}
        <FormControl fullWidth>
          <InputLabel id="status-select-label">{t('status')}</InputLabel>
          <Select
            labelId="status-select-label"
            value={selectedStatus}
            label={t('status')}
            onChange={(e) => setSelectedStatus(e.target.value as TransactionStatus)}
          >
            {Object.values(TransactionStatus).map((status) => {
              const colors = getTransactionStatusStyle(status);
              return (
                <MenuItem key={status} value={status}>
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-md font-medium ${colors.bg} ${colors.text}`}>
                    {getStatusLabel(status)}
                  </span>
                </MenuItem>
              );
            })}
          </Select>
        </FormControl>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isUpdating} variant="outlined">
          {t('cancel')}
        </Button>
        <Button onClick={handleUpdateStatus} variant="contained" disabled={isUpdating}>
          {isUpdating ? t('updating') : t('update_status')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
